import React, { useState } from 'react'
import Backdrop from './Backdrop'

const CARDS = [
  {
    badge: '01 · How to use it',
    title: (
      <>
        Just ask. Like messaging
        <br />
        your most trusted advisor.
      </>
    ),
    body:
      'Type naturally. Upload a document, screenshot, or file and ask Athena to analyse it. The more context you give, the sharper the answer.',
    tags: ['Upload files and images', 'Ask naturally', 'Build on answers'],
  },
  {
    badge: '02 · What it is',
    title: <>Your strategic brain.</>,
    body:
      "Athena is a custom business intelligence tool built on the Quantum Leap knowledge base with real-time research capability.",
    tags: [],
  },
  {
    badge: '03 · When to use it',
    title: (
      <>
        Before the big moment.
        <br />
        Between the sessions.
      </>
    ),
    body:
      'Use Athena when you need to think something through — a sales pitch, a problem, a strategy. It is your thinking partner.',
    tags: ['Before a client call', 'During research', 'When you are stuck'],
  },
  {
    badge: '04 · Why use it',
    title: (
      <>
        Because the right question
        <br />
        at the right time changes everything.
      </>
    ),
    body:
      'Athena is your thinking partner, available whenever you need it. Ask about strategy, content, business questions, or anything in between.',
    tags: ['Saves time', 'Better outcomes', 'Always available'],
  },
]

interface GuideModalProps {
  onClose: () => void
}

export default function GuideModal({ onClose }: GuideModalProps) {
  const [cur, setCur] = useState(0)
  const card = CARDS[cur]
  return (
    <Backdrop onClose={onClose}>
      <div
        style={{
          background: 'var(--white)',
          borderRadius: 20,
          width: 530,
          maxWidth: '94vw',
          boxShadow: 'var(--sh-lg)',
          overflow: 'hidden',
          animation: 'mIn .4s cubic-bezier(.16,1,.3,1) both',
        }}
      >
        <div
          style={{
            padding: '20px 26px 16px',
            borderBottom: '1px solid var(--bd2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h3
              style={{
                fontFamily: "'Playfair Display',serif",
                fontSize: 17,
                fontWeight: 500,
                color: 'var(--navy)',
              }}
            >
              How to use Athena
            </h3>
            <p style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 2 }}>
              4 things you should know
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              width: 28,
              height: 28,
              borderRadius: 7,
              border: '1px solid var(--bd)',
              background: 'transparent',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 15,
              color: 'var(--muted)',
            }}
          >
            ✕
          </button>
        </div>
        <div
          key={cur}
          style={{
            padding: '22px 26px 18px',
            minHeight: 200,
            animation: 'scIn .4s cubic-bezier(.16,1,.3,1) both',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                background: 'var(--blue-lt)',
                border: '1px solid var(--blue-bd)',
                borderRadius: 100,
                padding: '3px 11px',
                fontSize: 10,
                fontWeight: 600,
                color: 'var(--blue)',
                letterSpacing: 0.5,
                textTransform: 'uppercase',
                width: 'fit-content',
              }}
            >
              {card.badge}
            </div>
            <h4
              style={{
                fontFamily: "'Playfair Display',serif",
                fontSize: 19,
                fontWeight: 500,
                color: 'var(--navy)',
                lineHeight: 1.3,
              }}
            >
              {card.title}
            </h4>
            <p
              style={{
                fontSize: 13,
                color: 'var(--muted)',
                lineHeight: 1.72,
                maxWidth: 430,
              }}
            >
              {card.body}
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginTop: 4 }}>
              {card.tags.map((t) => (
                <span
                  key={t}
                  style={{
                    background: 'var(--bg)',
                    border: '1px solid var(--bd)',
                    borderRadius: 7,
                    padding: '5px 11px',
                    fontSize: 11.5,
                    color: 'var(--text)',
                  }}
                >
                  {t}
                </span>
              ))}
            </div>
          </div>
        </div>
        <div
          style={{
            padding: '14px 26px 20px',
            borderTop: '1px solid var(--bd2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                onClick={() => setCur(i)}
                style={{
                  width: cur === i ? 20 : 7,
                  height: 7,
                  borderRadius: cur === i ? 4 : '50%',
                  background: cur === i ? 'var(--navy)' : 'var(--bd)',
                  cursor: 'pointer',
                  transition: 'all .25s',
                }}
              />
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {cur > 0 && (
              <button
                type="button"
                onClick={() => setCur(cur - 1)}
                style={{
                  padding: '7px 16px',
                  borderRadius: 9,
                  border: '1px solid var(--bd)',
                  background: 'transparent',
                  fontSize: 12.5,
                  fontWeight: 500,
                  color: 'var(--muted)',
                  cursor: 'pointer',
                }}
              >
                ← Back
              </button>
            )}
            <button
              type="button"
              onClick={() => (cur < 3 ? setCur(cur + 1) : onClose())}
              style={{
                padding: '7px 16px',
                borderRadius: 9,
                border: 'none',
                background: 'var(--navy)',
                fontSize: 12.5,
                fontWeight: 500,
                color: 'var(--white)',
                cursor: 'pointer',
              }}
            >
              {cur === 3 ? 'Got it ✓' : 'Next →'}
            </button>
          </div>
        </div>
      </div>
    </Backdrop>
  )
}
